import {
  COLLECTION_INTERVAL_SECONDS,
  groupRuntimePointsByPod,
  hasMetricSpread,
  isSeriesTruncated,
  metricMean,
  type MetricUnit,
  type MetricWindow,
  type RuntimePoint,
} from "@/api";
import { formatBytes, formatNumber } from "@/utils";
import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { seriesSlotColor, type MetricSeries, type MetricSeriesPoint } from "./charts";
import { metricWindowLabel } from "./observabilityWindow";

/**
 * 조회 응답을 화면 계약으로 바꾸는 **순수 변환**이다.
 *
 * 계약에는 함정이 세 개 있다. 셋 모두 값을 그대로 그리면 화면이 틀린다. 각 패널이 나눠서
 * 처리하면 한 곳은 반드시 빠뜨린다. 그래서 이 모듈이 한 곳에서 처리한다.
 *
 * 1. **표본 없음을 0 으로 바꾸지 않는다.** `samples === 0` 이면 값이 아니라 값 없음이다. 타일은
 *    숫자 대신 값 없음 문구를 표시한다.
 * 2. **창 요약과 시계열의 구간이 다르다.** 요약은 창 전체다. `points` 는 서버 상한 500점으로
 *    잘린 끝부분이다. 차트 옆 캡션이 **실제 점 범위**를 표시해야 옆의 요약 숫자와 혼동하지 않는다.
 * 3. **런타임 500점은 pod 들이 나눠 쓴다.** pod 별로 나눠야 선을 그릴 수 있다. pod 마다 점 수가
 *    다르다는 사실이 캡션에 있어야 "이 pod 만 해상도가 낮다" 로 오해하지 않는다.
 *
 * Owns: 표시 문구·단위 포맷, 계열 산출, 구간 캡션.
 * Does not own: wire 타입과 다상태 판독기(`@/api/observability`). 렌더링(패널·차트).
 */

/** 값이 없는 자리의 표기다. 앱 전체의 빈칸 토큰과 같다. */
export const EMPTY_TOKEN = "—";

/** 표본이 하나도 없는 지표의 문구다. "0" 과 분명히 구분되는 말을 쓴다. */
export const NO_SAMPLES_TEXT = "표본 없음";

/** 값을 표시 문구로 바꾼다. 지표마다 단위가 달라서 호출부가 전달한다. */
export type MetricFormatter = (value: number) => string;

export const formatCount: MetricFormatter = (value) => formatNumber(Math.round(value));

export const formatBytesValue: MetricFormatter = (value) => formatBytes(value, EMPTY_TOKEN);

/** 밀리초 지표다. 1초를 넘으면 초로 표시한다. ms 네 자리는 자릿수만 늘고 읽기 어렵다. */
export const formatMillis: MetricFormatter = (value) =>
  value >= 1000 ? `${(value / 1000).toFixed(2)}s` : `${value.toFixed(value < 10 ? 2 : 0)}ms`;

/** 초 지표다. 트랜잭션 나이처럼 분·시 단위까지 커지는 값에 쓴다. */
export const formatSeconds: MetricFormatter = (value) => {
  const seconds = Math.max(0, Math.round(value));
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

/** 0..1 비율 지표다. */
export const formatRatio: MetricFormatter = (value) => `${(value * 100).toFixed(1)}%`;

/**
 * 배수 지표다. 읽기 증폭처럼 1 이 기준선이고 상한이 없는 값에 쓴다.
 *
 * `formatRatio` 는 쓸 수 없다. 그 함수는 0..1 을 백분율로 바꾸므로 8.9 가 `890.0%` 가 된다.
 * 백분율로 읽으면 기준선이 100% 인지 0% 인지 불분명해진다.
 */
export const formatTimes: MetricFormatter = (value) => `${value.toFixed(1)}×`;

/** CPU millicore 지표다. */
export const formatMillicores: MetricFormatter = (value) => `${formatNumber(Math.round(value))}m`;

/** 창 요약 하나를 표시 문구로 바꾼 결과다. */
export type MetricUnitView = {
  /** 큰 수치다. 평균이고, 표본이 없으면 값 없음 문구다. */
  value: string;
  /** 봉우리와 산포를 담은 보조 한 줄이다. 표본이 없으면 빈 문자열이고, 호출부가 생략한다. */
  hint: string;
  /** 봉우리 시각 문구다. `hint` 에서 시각을 뺀 곳(표 셀)은 툴팁에 표시한다. 없으면 빈 문자열이다. */
  peakAt: string;
};

export type MetricUnitViewOptions = {
  /**
   * 표 칸 밀도로 줄일지 정한다.
   *
   * 타일은 기본값(`false`)을 쓴다. 타일 여섯 개를 한 화면에서 보므로 폭이 충분하다. 봉우리는
   * 값보다 **시각**이 다음 행동을 정한다. 표 칸은 `true` 를 쓴다. 사용자는 20행을 빠르게 읽으므로
   * 행마다 시각까지 읽지 않는다. 시각을 넣으면 열 폭 때문에 잘려 산포가 사라진다.
   *
   * 줄이는 것은 두 가지다:
   * - 봉우리 시각. `peakAt` 으로 옮겨 툴팁에 표시한다.
   * - 표본이 하나일 때의 문구(`표본 1개(산포 없음)` → `표본 1개`). 이 문구도 열 폭 때문에
   *   `산포 없` 으로 잘린다.
   */
  compact?: boolean;
  /**
   * 큰 수치로 무엇을 표시할지 정한다.
   *
   * 기본값은 `"mean"` 이다. 대부분의 지표는 "평상시 얼마인가" 를 묻기 때문에 평균이 창을 대표한다.
   * `"peak"` 은 이름 자체가 최댓값을 묻는 지표(「최장 트랜잭션」)에 쓴다. 최댓값들의 평균은
   * 실제 어떤 값도 가리키지 않는다. 그 자리에 평균을 두면, 대부분의 tick 이 0 인 지표는 `0s` 가
   * 되어 "그런 일이 없었다" 로 읽힌다. 실제로는 창 안에 24시간짜리가 있었다.
   */
  headline?: "mean" | "peak";
};

/**
 * 창 요약을 타일 문구로 바꾼다.
 *
 * 큰 수치는 **평균**이다. 평균이 창 전체를 대표하기 때문이다. 봉우리는 hint 에 둔다. 봉우리
 * 하나가 큰 자리를 차지하면 평상시 수준을 읽을 수 없기 때문이다. 봉우리는 값과 **시각**을 함께
 * 표시한다. 값만으로는 다음 행동을 정할 수 없다. 사용자는 그 시각으로 같은 구간의 다른 지표를
 * 확인한다. 이것이 이 화면의 용도다.
 *
 * 산포는 표본이 둘 이상일 때만 적는다. 표본이 하나면 `stdev` 는 0 이 아니라 정의되지 않는다.
 * "±0" 으로 적으면 "값이 변하지 않았다" 는 없는 사실이 된다.
 *
 * `unit` 자체가 없는 경우도 "표본 없음" 으로 표시한다. 그 판정은 `metricMean` 이 소유한다.
 */
export function describeMetricUnit(
  unit: MetricUnit,
  format: MetricFormatter,
  { compact = false, headline = "mean" }: MetricUnitViewOptions = {},
): MetricUnitView {
  const mean = metricMean(unit);
  if (mean === null) {
    return { value: NO_SAMPLES_TEXT, hint: "", peakAt: "" };
  }
  const parts: string[] = [];
  const peakAtMs = parseUtcMs(unit.peak.at);
  const peakAt = peakAtMs === null ? "" : formatDateTime(peakAtMs);
  // 봉우리를 큰 수로 표시하는 지표에서는 평균을 보조로 표시한다. 둘 중 하나는 반드시 hint 에
  // 남아야 한다. 최댓값만 보면 평상시 수준을 알 수 없고, 평균만 보면 사고를 알 수 없다.
  const peakUp = headline === "peak" && unit.peak.value !== null;
  if (peakUp) {
    parts.push(`평소 ${format(mean)}`);
  } else if (unit.peak.value !== null) {
    const when = !compact && peakAt !== "" ? ` (${peakAt})` : "";
    parts.push(`최대 ${format(unit.peak.value)}${when}`);
  }
  if (hasMetricSpread(unit) && unit.stdev !== null) {
    parts.push(`산포 ±${format(unit.stdev)}`);
  } else if (unit.samples === 1) {
    parts.push(compact ? "표본 1개" : "표본 1개(산포 없음)");
  }
  return {
    value: format(peakUp ? (unit.peak.value ?? mean) : mean),
    hint: parts.join(" · "),
    peakAt,
  };
}

/** 시계열 캡션의 내용이다. 이 차트가 실제로 포함하는 구간과 점 수다. */
export type SeriesCoverage = {
  /** 카드 부제로 그대로 쓰는 한 줄이다. */
  caption: string;
  /** 점이 하나도 없으면 참이다. 이때 호출부는 차트 대신 빈 문구를 그린다. */
  isEmpty: boolean;
  /** 서버 상한 때문에 창의 앞부분이 잘렸으면 참이다. */
  isTruncated: boolean;
};

/**
 * 시계열이 실제로 포함하는 구간을 문구로 만든다.
 *
 * **잘렸는지를 반드시 표시한다.** `points` 는 최신 500점이라 5분 주기면 약 41시간이다. `30d` 창을
 * 골라도 차트는 마지막 41시간만 그린다. 그러나 바로 옆 타일의 평균은 30일 전체의 값이다. 캡션이
 * 이 사실을 표시하지 않으면 두 숫자가 같은 구간의 값으로 읽힌다.
 */
export function describeSeriesCoverage(
  points: readonly { captured_at: string }[],
  window: MetricWindow,
): SeriesCoverage {
  if (points.length === 0) {
    return {
      caption: `${metricWindowLabel(window)} · 표본 없음`,
      isEmpty: true,
      isTruncated: false,
    };
  }
  const first = parseUtcMs(points[0].captured_at);
  const last = parseUtcMs(points[points.length - 1].captured_at);
  const span =
    first === null || last === null
      ? metricWindowLabel(window)
      : `${formatDateTime(first)} ~ ${formatDateTime(last)}`;
  const isTruncated = isSeriesTruncated(points);
  const truncation = isTruncated ? " · 상한에 잘려 창 앞부분은 빠져 있다" : "";
  return {
    caption: `${span} · ${formatNumber(points.length)}점${truncation}`,
    isEmpty: false,
    isTruncated,
  };
}

/** 지표 하나를 계열 하나로 만든다. 값이 `null` 인 tick 은 그대로 남겨 선을 끊는다. */
export function toSeries<P extends { captured_at: string }>(
  points: readonly P[],
  options: {
    key: string;
    label: string;
    slot: number;
    pick: (point: P) => number | null;
  },
): MetricSeries {
  const series: MetricSeriesPoint[] = [];
  for (const point of points) {
    const atMs = parseUtcMs(point.captured_at);
    if (atMs === null) {
      continue;
    }
    series.push({ atMs, value: options.pick(point) });
  }
  return {
    key: options.key,
    label: options.label,
    color: seriesSlotColor(options.slot),
    points: series,
  };
}

/** pod 별 계열과 각 계열의 점 수다. 캡션은 이 값으로 pod 당 해상도를 표시한다. */
export type RuntimeSeriesGroup = {
  series: readonly MetricSeries[];
  /** `registry-0 166점 · registry-1 167점` 형식이다. pod 이 하나면 빈 문자열이다. */
  perPodNote: string;
};

/**
 * 런타임 시계열을 pod 별 계열로 나눈다.
 *
 * 모든 pod 이 500점 상한을 함께 쓴다. 그래서 pod 이 늘수록 pod 당 해상도가 반비례로 낮아진다.
 * 화면에 그 사실이 없으면 "이 pod 만 데이터가 드물다" 는 틀린 결론이 나온다.
 */
export function toRuntimeSeries(
  points: readonly RuntimePoint[],
  pick: (point: RuntimePoint) => number | null,
): RuntimeSeriesGroup {
  const byPod = groupRuntimePointsByPod(points);
  const series: MetricSeries[] = [];
  const notes: string[] = [];
  let slot = 0;
  for (const [podName, podPoints] of byPod) {
    series.push(toSeries(podPoints, { key: podName, label: podName, slot, pick }));
    notes.push(`${podName} ${formatNumber(podPoints.length)}점`);
    slot += 1;
  }
  return { series, perPodNote: byPod.size > 1 ? notes.join(" · ") : "" };
}

/** 수집 건강 readout 한 줄과 그 톤이다. */
export type CollectionHealthView = {
  /** `288 사이클 · 실패 0 · 최대 공백 5분 · 마지막 2026-08-27 09:41` 형식이다. */
  text: string;
  /** 실패·부분 사이클이 있거나 주기보다 훨씬 긴 공백이 있으면 참이다. 호출부가 경고 톤을 적용한다. */
  isDegraded: boolean;
};

/**
 * 수집 건강을 한 줄로 요약한다. `hasCyclesInWindow` 가 거짓이면 이 함수를 호출하지 않는다.
 * 그때는 화면 전체에 안내 문구를 표시한다.
 */
export function describeCollectionHealth(summary: {
  cycles: number;
  failed_cycles: number;
  partial_cycles: number;
  last_captured_at: string | null;
  max_gap_seconds: number | null;
}): CollectionHealthView {
  const parts = [`${formatNumber(summary.cycles)} 사이클`];
  parts.push(`실패 ${formatNumber(summary.failed_cycles)}`);
  if (summary.partial_cycles > 0) {
    parts.push(`부분 ${formatNumber(summary.partial_cycles)}`);
  }
  parts.push(
    summary.max_gap_seconds === null
      ? "최대 공백 —"
      : `최대 공백 ${formatSeconds(summary.max_gap_seconds)}`,
  );
  const lastMs = parseUtcMs(summary.last_captured_at);
  parts.push(`마지막 ${lastMs === null ? EMPTY_TOKEN : formatDateTime(lastMs)}`);
  const gapDegraded =
    summary.max_gap_seconds !== null && summary.max_gap_seconds > COLLECTION_INTERVAL_SECONDS * 2;
  return {
    text: parts.join(" · "),
    isDegraded: summary.failed_cycles > 0 || summary.partial_cycles > 0 || gapDegraded,
  };
}

/**
 * 마지막으로 값이 있는 점을 찾는다.
 *
 * 창 요약 필드가 없는 지표(DB 크기)는 이 점이 유일한 근거다. 최신 점이 `null` 이어도 직전
 * 스냅샷의 관측값은 남아 있다. 그래서 뒤에서부터 찾는다. 최신 한 점만 보고 빈칸을 그리면
 * "크기를 모른다" 는 틀린 상태가 된다. 값과 함께 **그 시각**을 반환한다. 이 값은 창을 대표하지
 * 않기 때문이다. 라벨에 측정 시각이 있어야 옆 타일과 혼동하지 않는다.
 */
export function lastNonNullPoint<P extends { captured_at: string }>(
  points: readonly P[],
  pick: (point: P) => number | null,
): { value: number; atMs: number } | null {
  for (let index = points.length - 1; index >= 0; index -= 1) {
    const value = pick(points[index]);
    const atMs = parseUtcMs(points[index].captured_at);
    if (value !== null && atMs !== null) {
      return { value, atMs };
    }
  }
  return null;
}

/**
 * fingerprint 십진수를 64비트 hex 표기로 바꾼다.
 *
 * 표시의 기준은 **십진수**다. `pg_stat_statements.queryid` 는 부호 있는 bigint 라서 조회도
 * `WHERE queryid = -4546...` 로 한다. 사용자가 복사해 바로 쓸 값은 십진수다. hex 는 길이가
 * 16자로 고정이라 눈으로 비교하기 쉽다. 그래서 툴팁에만 추가한다.
 *
 * 음수는 2의 보수로 바꾼다(`asUintN`). Postgres 는 같은 비트를 부호 있는 값으로 읽을 뿐이다.
 * 그래서 비트열 자체는 같다. 값을 BigInt 로 다루는 이유: Number 로 변환하면 2^53 위에서
 * 자릿수를 잃고, hex 도 함께 틀린다.
 *
 * 파싱할 수 없는 값은 빈 문자열이다. 이때 호출부는 툴팁을 추가하지 않는다.
 */
export function fingerprintHex(decimal: string): string {
  // `BigInt("")` 는 예외를 던지지 않고 `0n` 을 준다. 빈 값이 경고 없이 0 으로 보이지 않게 먼저 막는다.
  if (decimal.trim() === "") {
    return "";
  }
  try {
    return `0x${BigInt.asUintN(64, BigInt(decimal)).toString(16).toUpperCase().padStart(16, "0")}`;
  } catch {
    return "";
  }
}

/** 표의 숫자 하나를 표시한다. 계약상 `null` 은 "서버가 세지 못했다" 라서 0 으로 바꾸지 않는다. */
export function formatOptional(value: number | null, format: MetricFormatter): string {
  return value === null ? EMPTY_TOKEN : format(value);
}

/** 표의 시각 칸이다. */
export function formatOptionalAt(iso: string | null): string {
  const ms = parseUtcMs(iso);
  return ms === null ? EMPTY_TOKEN : formatDateTime(ms);
}
